import React, { useState, useEffect, useMemo } from 'react';
import { Loader, Text, Stack } from '@mantine/core';
import YearPicker from './YearPicker';
import CroppedFieldsTree from './CroppedFieldsTree';
import CroppedFieldsGrid from './CroppedFieldsGrid';
import CarbonIntensityGrid from './CarbonIntensityGrid';
import { API_ENDPOINTS } from '../constants/api';
import { getOrganizationId } from '../utils/jwtUtils';
import './AnnualInformation.css';

interface AnnualInformationProps {
  userToken: string;
  organizationName: string;
  selectedOrganizationId?: string | null;
  isSuperUser?: boolean;
  engagementLevel?: number;
}

const AnnualInformation: React.FC<AnnualInformationProps> = ({ 
  userToken, 
  organizationName,
  selectedOrganizationId,
  isSuperUser,
  engagementLevel = 1
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [activeView, setActiveView] = useState<'map' | 'details' | 'carbon'>('map');
  const [fieldsWithCrops, setFieldsWithCrops] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoadedForYear, setDataLoadedForYear] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<{ type: string; id: string } | null>(null);
  
  // Carbon intensity calculation state
  const [isCalculatingExcel, setIsCalculatingExcel] = useState(false);
  const [isCalculatingAPI, setIsCalculatingAPI] = useState(false);
  
  // Crop type selection state
  const [selectedCropTypes, setSelectedCropTypes] = useState<string[]>([]);
  
  // Modal state
  const [showCropSelectionModal, setShowCropSelectionModal] = useState(false);
  
  // Component refresh key for clean re-renders
  const [componentRefreshKey, setComponentRefreshKey] = useState(0);
  
  // Get unique crop types from current year's data, filtered to FD-CIC-supported crops
  const availableCropTypes = useMemo(() => {
    if (!fieldsWithCrops || fieldsWithCrops.length === 0) {
      //console.log('🌾 No fieldsWithCrops data available for crop types');
      return [];
    }
    
    // USDA FD-CIC workbooks support these crop types
    const fdcicSupportedCrops = ['Corn', 'Soybean', 'Sorghum', 'Canola'];
    
    const uniqueCrops = new Set<string>();
    fieldsWithCrops.forEach((field) => {
      if (!field.crop) return;
      // Normalize Soybeans → Soybean for selection
      const crop =
        field.crop === 'Soybeans' ? 'Soybean' : field.crop;
      if (fdcicSupportedCrops.includes(crop)) {
        uniqueCrops.add(crop);
      }
    });
    
    const cropArray = Array.from(uniqueCrops).sort();
    return cropArray;
  }, [fieldsWithCrops]);

  const loadFieldsWithCrops = async () => {
    // Don't reload if we already have data for this year AND organization
    // This prevents unnecessary API calls when switching between tabs
    const targetOrgId = selectedOrganizationId || getOrganizationId(userToken);
    if (!targetOrgId) {
      return;
    }
    
    const cacheKey = `${selectedYear}-${targetOrgId}`;
    
    if (dataLoadedForYear === cacheKey && fieldsWithCrops.length > 0) {
      return;
    }

    setIsLoading(true);
    try {
      // Always include organization_id for consistency and security
      // For Super Users: use selectedOrganizationId, for normal users: extract from JWT
      const organizationParam = `&organization_id=${targetOrgId}`;
        
      const url = `${API_ENDPOINTS.FIELDS_WITH_CROPS}?year=${selectedYear}${organizationParam}`;
        
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });


      if (response.ok) {
        const data = await response.json();
        setFieldsWithCrops(data);
        setDataLoadedForYear(cacheKey);
      } else {
        const errorText = await response.text();
        console.error('🌾 Error loading fields with crops:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
      }
    } catch (error) {
      console.error('🌾 Exception loading fields with crops:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Manual refresh function for when user explicitly wants to reload data
  // This can be called from child components or UI buttons when fresh data is needed
  const refreshData = async () => {
    const targetOrgId = selectedOrganizationId || getOrganizationId(userToken);
    if (!targetOrgId) {
      console.error('🌾 No organization ID available for refresh');
      return;
    }
    
    // Clear the cache and force a fresh load
    setDataLoadedForYear(null);
    setFieldsWithCrops([]); // Clear existing data to force reload
    
    // Force a fresh API call by calling the API directly instead of loadFieldsWithCrops
    setIsLoading(true);
    try {
      const organizationParam = `&organization_id=${targetOrgId}`;
      const url = `${API_ENDPOINTS.FIELDS_WITH_CROPS}?year=${selectedYear}${organizationParam}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFieldsWithCrops(data);
        setDataLoadedForYear(`${selectedYear}-${targetOrgId}`);
      } else {
        const errorText = await response.text();
        console.error('🌾 Error loading fields with crops:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
      }
    } catch (error) {
      console.error('🌾 Exception loading fields with crops:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Light refresh function that updates data without showing loading state
  // Used for crop edits to avoid the "Loading..." flash
  const lightRefreshData = async () => {
    const targetOrgId = selectedOrganizationId || getOrganizationId(userToken);
    if (!targetOrgId) {
      console.error('🌾 No organization ID available for light refresh');
      return;
    }
    
    try {
      const organizationParam = `&organization_id=${targetOrgId}`;
      const url = `${API_ENDPOINTS.FIELDS_WITH_CROPS}?year=${selectedYear}${organizationParam}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFieldsWithCrops(data);
        setDataLoadedForYear(`${selectedYear}-${targetOrgId}`);
      } else {
        const errorText = await response.text();
        console.error('🌾 Error in light refresh:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
      }
    } catch (error) {
      console.error('🌾 Exception in light refresh:', error);
    }
  };

  // Simple refresh function that forces component re-render
  const refreshComponents = () => {
    setComponentRefreshKey(prev => prev + 1);
  };

  // Handle fetching carbon intensity scores using Excel
  const handleFetchCarbonScores = async () => {
    setIsCalculatingExcel(true);
    try {
      // Get organization ID and year - much simpler approach
      const targetOrgId = selectedOrganizationId || getOrganizationId(userToken);
      if (!targetOrgId) {
        throw new Error('No organization ID available');
      }
      
      // Call the new Excel-based carbon-intensity endpoint for all fields
      // Add organization_id parameter for Super Users
      const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
      const response = await fetch(`${API_ENDPOINTS.CARBON_INTENSITY}/calculate-excel-all${organizationParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ 
          year: selectedYear,
          cropTypes: selectedCropTypes // Include selected crop types
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🌾 Excel carbon intensity calculation completed:', result);
        
        // Refresh the grid to show updated carbon scores
        await refreshData();
      } else {
        const errorText = await response.text();
        console.error('🌾 Error calculating carbon intensity scores:', errorText);
        throw new Error(`API Error: ${response.status} ${errorText}`);
      }
      
    } catch (error) {
      console.error('🌾 Error calculating carbon intensity scores:', error);
      // TODO: Show user-friendly error message
    } finally {
      setIsCalculatingExcel(false);
    }
  };

  // COMMENTED OUT: Handle fetching carbon intensity scores using External API
  /*
  const handleFetchCarbonScoresAPI = async () => {
    setIsCalculatingAPI(true);
    try {
      // Get organization ID and year - much simpler approach
      const targetOrgId = selectedOrganizationId || getOrganizationId(userToken);
      if (!targetOrgId) {
        throw new Error('No organization ID available');
      }
      
      // Call the external API carbon-intensity endpoint for all fields
      // Add organization_id parameter for Super Users
      const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
      const response = await fetch(`${API_ENDPOINTS.CARBON_INTENSITY}/calculate-business${organizationParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ 
          year: selectedYear,
          cropTypes: selectedCropTypes // Include selected crop types
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🌾 External API carbon intensity calculation completed:', result);
        
        // Refresh the grid to show updated carbon scores
        await refreshData();
      } else {
        const errorText = await response.text();
        console.error('🌾 Error calculating carbon intensity scores via API:', errorText);
        throw new Error(`API Error: ${response.status} ${errorText}`);
      }
      
    } catch (error) {
      console.error('🌾 Error calculating carbon intensity scores via API:', error);
      // TODO: Show user-friendly error message
    } finally {
      setIsCalculatingAPI(false);
    }
  };
  */

  useEffect(() => {
    loadFieldsWithCrops();
  }, [selectedYear, userToken, selectedOrganizationId]); // Added selectedOrganizationId dependency

  // Trigger data loading when modal opens to ensure we have fresh data
  useEffect(() => {
    if (showCropSelectionModal) {
      loadFieldsWithCrops();
    }
  }, [showCropSelectionModal, selectedYear, userToken, selectedOrganizationId]);
  // Data will reload when year changes, userToken changes, or organization changes (for Super Users)

  return (
    <div className="annual-information">
      {/* Full-screen loading overlay when calculating carbon scores */}
      {(isCalculatingExcel || isCalculatingAPI) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '40px 50px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              minWidth: '320px',
            }}
          >
            <Stack align="center" spacing="md">
              <Loader size="xl" />
              <Text size="lg" fw={600}>Calculating Carbon Intensity</Text>
              <Text size="sm" c="dimmed">Please wait while we process your data...</Text>
              {selectedCropTypes.length > 0 && (
                <Text size="xs" c="dimmed" mt="xs">
                  Processing: {selectedCropTypes.join(', ')}
                </Text>
              )}
            </Stack>
          </div>
        </div>
      )}
      <div className="annual-header">
        <div className="header-left">
          <YearPicker 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </div>
        <div className="header-right">
          <button
            className={`view-button ${activeView === 'map' ? 'active' : ''}`}
            onClick={() => setActiveView('map')}
          >
            Cropped Fields Map
          </button>
          <button
            className={`view-button ${activeView === 'details' ? 'active' : ''}`}
            onClick={() => setActiveView('details')}
          >
            Cropped Fields Details
          </button>
          {engagementLevel > 1 && (
            <button
              className={`view-button ${activeView === 'carbon' ? 'active' : ''}`}
              onClick={() => setActiveView('carbon')}
            >
              Carbon Intensity Information
            </button>
          )}

 
                    
          {/* Fetch Carbon Intensity Scores button - only show for Level 2+ users */}
          {engagementLevel > 1 && (
            <div className="carbon-calculation-controls">
              <button
                className="fetch-carbon-scores-btn"
                onClick={() => setShowCropSelectionModal(true)}
                disabled={isCalculatingAPI || isCalculatingExcel}
                title="Select crops and calculate carbon intensity scores"
              >
                Fetch Carbon Scores
              </button>
              <a
                href="https://ravahcarbon.tawk.help/article/adding-a-crop-record"
                target="_blank"
                rel="noopener noreferrer"
                className="help-button"
                title="Open Knowledge Base article: Adding a Crop Record"
              >
                Help
              </a>
            </div>
          )}

        </div>
      </div>

      <div className="annual-content">
        {/* All components are always rendered but hidden/shown with CSS to prevent unmounting and data reloading */}
        <div className={`view-panel ${activeView === 'map' ? 'active' : 'hidden'}`}>
          <CroppedFieldsTree
            key={`tree-${componentRefreshKey}`}
            selectedYear={selectedYear}
            userToken={userToken}
            fieldsWithCrops={fieldsWithCrops}
            selectedItem={selectedItem}
            onItemSelect={setSelectedItem}
            onRefresh={refreshData}
            onLightRefresh={lightRefreshData}
            onComponentRefresh={refreshComponents}
            selectedOrganizationId={selectedOrganizationId}
            isSuperUser={isSuperUser}
            engagementLevel={engagementLevel}
          />
        </div>
        <div className={`view-panel ${activeView === 'details' ? 'active' : 'hidden'}`}>
          <CroppedFieldsGrid
            selectedYear={selectedYear}
            fieldsWithCrops={fieldsWithCrops}
            isLoading={isLoading}
            userToken={userToken}
            selectedOrganizationId={selectedOrganizationId || undefined}
            engagementLevel={engagementLevel}
            onDataRefresh={async () => {
              // Reload the fieldsWithCrops data with fresh data
              await refreshData();
            }}
          />
        </div>
        {engagementLevel > 1 && (
          <div className={`view-panel ${activeView === 'carbon' ? 'active' : 'hidden'}`}>
            <CarbonIntensityGrid
              selectedYear={selectedYear}
              userToken={userToken}
              selectedOrganizationId={selectedOrganizationId || undefined}
              fieldsWithCrops={fieldsWithCrops}
              isLoading={isLoading}
              engagementLevel={engagementLevel}
              onDataRefresh={async () => {
                // Reload the fieldsWithCrops data with fresh data
                await refreshData();
              }}
            />
          </div>
        )}
      </div>
      
      {/* Crop Selection Modal */}
      {showCropSelectionModal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            if (!isCalculatingExcel && !isCalculatingAPI) {
              setShowCropSelectionModal(false);
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Crops for Carbon Calculation</h3>
              <button 
                className="modal-close-btn"
                onClick={() => {
                  if (!isCalculatingExcel && !isCalculatingAPI) {
                    setShowCropSelectionModal(false);
                  }
                }}
                disabled={isCalculatingExcel || isCalculatingAPI}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Choose which crop types to include in the carbon intensity calculation. 
                Only FD-CIC-supported crops (Corn, Soybean, Sorghum, Canola) are shown.
                Leave empty to process all supported crops for {selectedYear}.
              </p>
              
              <div className="crop-selection-container">
                {isLoading ? (
                  <div className="loading-message">
                    🔄 Loading field data for {selectedYear}...
                  </div>
                ) : (
                  <>
                    <label htmlFor="modal-crop-select" className="crop-select-label">
                      Crop Types:
                    </label>
                    <div className="crop-checkboxes">
                      {availableCropTypes.map(crop => (
                    <label key={crop} className="crop-checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedCropTypes.includes(crop)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCropTypes([...selectedCropTypes, crop]);
                          } else {
                            setSelectedCropTypes(selectedCropTypes.filter(c => c !== crop));
                          }
                        }}
                        className="crop-checkbox"
                        disabled={isCalculatingExcel || isCalculatingAPI}
                      />
                      <span className="crop-checkbox-label">{crop}</span>
                    </label>
                  ))}
                </div>
                
                {selectedCropTypes.length > 0 && (
                  <div className="selected-crops-info">
                    Selected: {selectedCropTypes.join(', ')}
                  </div>
                    )}
                    
                    {availableCropTypes.length === 0 && (
                      <div className="no-crops-warning">
                        ⚠️⃣ No FD-CIC-supported crops found for {selectedYear}. 
                        Check if fields are added and contain supported crops (Corn, Soybean, Sorghum, Canola).
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="modal-cancel-btn"
                onClick={() => {
                  if (!isCalculatingExcel && !isCalculatingAPI) {
                    setShowCropSelectionModal(false);
                  }
                }}
                disabled={isCalculatingExcel || isCalculatingAPI}
              >
                Cancel
              </button>
              <button 
                className="modal-calculate-btn modal-calculate-excel-btn"
                onClick={async () => {
                  // Store the selected crops before closing modal
                  const cropsToProcess = selectedCropTypes;
                  
                  // Don't close modal immediately - show calculating state first
                  if (!isCalculatingExcel && !isCalculatingAPI) {
                    await handleFetchCarbonScores();
                    // Only close modal after calculation completes
                    setShowCropSelectionModal(false);
                  }
                }}
                disabled={isCalculatingExcel || isCalculatingAPI || availableCropTypes.length === 0}
              >
                {isCalculatingExcel ? 'Calculating...' : 'Fetch Carbon Scores'}
              </button>
        {/* COMMENTED OUT: External API carbon intensity calculation - using Microsoft Excel service instead */}
        {/*
              <button 
                className="modal-calculate-btn modal-calculate-api-btn"
                onClick={async () => {
                  // Store the selected crops before closing modal
                  const cropsToProcess = selectedCropTypes;
                  
                  // Don't close modal immediately - show calculating state first
                  if (!isCalculatingExcel && !isCalculatingAPI) {
                    await handleFetchCarbonScoresAPI();
                    // Only close modal after calculation completes
                    setShowCropSelectionModal(false);
                  }
                }}
                disabled={isCalculatingExcel || isCalculatingAPI}
              >
                {isCalculatingAPI ? 'Calculating...' : 'Fetch Carbon Scores (API)'}
              </button>
              */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnualInformation;
